import { useEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2 } from "@btnet/services-btnet/handlers/entity-records/entity-records";
import { EntityType } from "@btnet/services-btnet/handlers/models/entityType";

import { BdsIconSpecifications, BdsText } from "@buildertrend/components";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";

import { arePlansLinkingSupportedForEntity } from "entity/specification/common/SpecificationsPanel/SpecificationsPanelLayout.utilities";

export interface ISpecificationsLinkButtonProps {
    /**
     * The external ID of the source entity
     * If undefined or falsy, shows "Save to add specifications" text
     */
    externalId: string | undefined;
    /**
     * Whether the user can edit (add/remove) specifications
     * Generally this should just be the same as the current user's edit permission on the source entity and not tied to the source entity state.
     */
    canEdit: boolean;
    /**
     * Whether the specifications panel is currently open
     */
    isPanelOpen: boolean;
    /**
     * Callback to toggle the specifications panel
     */
    onTogglePanel: () => void;
    /**
     * The entity type of the source entity
     */
    sourceEntityType?: EntityType;
}

/**
 * Button to toggle the specifications side panel
 */
export const SpecificationsLinkButton = ({
    externalId,
    canEdit,
    isPanelOpen,
    onTogglePanel,
    sourceEntityType = EntityType.None,
}: ISpecificationsLinkButtonProps) => {
    const plansSupported = arePlansLinkingSupportedForEntity(sourceEntityType);

    const batchEntityTypes = plansSupported
        ? [EntityType.Specification, EntityType.PlanSet, EntityType.PlanPage]
        : [EntityType.Specification];

    const { data: batchData } = useEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2(
        externalId ?? "",
        { targetEntityTypes: batchEntityTypes },
        { query: { enabled: !!externalId } },
    );

    const count =
        batchData?.data.entitiesByType.reduce(
            (sum, g) => (g.entityType === EntityType.PlanPage ? sum : sum + g.entityRecords.length),
            0,
        ) ?? 0;

    if (!externalId) {
        return (
            <BdsText
                text={plansSupported ? "Save to add plans and specs" : "Save to add specs"}
                size="normal-md"
            />
        );
    }

    const withCount = (text: string) => (count > 0 ? `${text} (${count})` : text);
    const specsButtonText = withCount(canEdit ? "View and link specs" : "View specs");
    const specsAndPlansButtonText = withCount("View Plans and Specs");

    return (
        <BTButton
            type="secondary"
            icon={<BdsIconSpecifications />}
            id="specifications-panel-button"
            onClick={onTogglePanel}
            data-testid="specifications-panel-button"
            aria-pressed={isPanelOpen}
        >
            {plansSupported ? specsAndPlansButtonText : specsButtonText}
        </BTButton>
    );
};
